import React, { useState } from 'react';
import { FaVideo, FaPlay, FaPause, FaCrown, FaShare, FaEye, FaRobot } from 'react-icons/fa';
import API_CONFIG from '../config/api';
import { encodeId } from '../utils/idMasker';
import { useCredits } from '../contexts/CreditsContext';
import ShareModal from './ShareModal';
import InsufficientCreditsModal from './credits/InsufficientCreditsModal';
import './VideoList.css';

const VideoList = ({ videos = [], onVideoPlay, loading = false }) => {
    const { creditBalance, hasSufficientCredits } = useCredits();
    const [currentlyPlaying, setCurrentlyPlaying] = useState(null);
    const [imageErrors, setImageErrors] = useState({});
    const [shareModal, setShareModal] = useState({ isOpen: false, video: null });
    const [showInsufficientCreditsModal, setShowInsufficientCreditsModal] = useState(false);
    const [selectedVideo, setSelectedVideo] = useState(null);

    // Handle share button click
    const handleShare = (video, e) => {
        e.stopPropagation();
        setShareModal({ isOpen: true, video });
    };

    // Get share URL for modal
    const getShareUrl = (video) => {
        const pageSlug = video.page_slug || 'video';
        const videoId = video.video?.id || video.id;
        const encodedId = encodeId(videoId);
        return `${window.location.origin}/video/${pageSlug}/${encodedId}`;
    };

    // Handle video card click - check credits for premium content
    const handleVideoCardClick = (video, e) => {
        // Don't trigger if clicking on share button
        if (e.target.closest('.video-share-btn')) {
            return;
        }

        // Check if premium and user has insufficient credits
        if (video.is_paid) {
            const videoPrice = video.price || 1;
            if (!hasSufficientCredits(videoPrice)) {
                // Show modal and prevent any further action
                e.preventDefault();
                e.stopPropagation();
                setSelectedVideo(video);
                setShowInsufficientCreditsModal(true);
                return;
            }
        }

        // If not premium or has credits, play the video
        handlePlayClick(video);
    };

    // Handle video play/pause
    const handlePlayClick = async (video) => {
        try {
            if (currentlyPlaying === video.id) {
                // Pause current video
                setCurrentlyPlaying(null);
                if (onVideoPlay) {
                    onVideoPlay(null);
                }
            } else {
                // Play new video
                setCurrentlyPlaying(video.id);
                if (onVideoPlay) {
                    await onVideoPlay(video);
                }
            }
        } catch (error) {
            console.error('Error playing video:', error);
            setCurrentlyPlaying(null);
        }
    };

    // Handle image load errors
    const handleImageError = (videoId) => {
        setImageErrors(prev => ({ ...prev, [videoId]: true }));
    };

    // Construct full image URL from relative path
    const getImageUrl = (coverImageUrl) => {
        if (!coverImageUrl) return null;
        if (coverImageUrl.startsWith('http://') || coverImageUrl.startsWith('https://')) {
            return coverImageUrl;
        }
        return `${API_CONFIG.BASE_URL}${coverImageUrl}`;
    };

    // Format duration from seconds to MM:SS or HH:MM:SS
    const formatDuration = (seconds) => {
        if (!seconds) return '0:00';

        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = Math.floor(seconds % 60);

        if (hours > 0) {
            return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
        }
        return `${minutes}:${secs.toString().padStart(2, '0')}`;
    };

    if (loading) {
        return (
            <div className="video-list-container">
                <div className="video-list-loading">
                    <div className="loading-spinner"></div>
                    <p>Loading videos...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="video-list-container">
            {videos.length > 0 ? (
                <div className="video-grid">
                    {videos.map((video) => (
                        <div 
                            key={video.id} 
                            className="video-item"
                            onClick={(e) => handleVideoCardClick(video, e)}
                            style={{ cursor: 'pointer' }}
                            data-premium={video.is_paid ? "true" : "false"}
                            data-required-credits={video.is_paid ? (video.price || 1) : 0}
                        >
                            {/* Video Thumbnail */}
                            <div className="video-thumbnail">
                                {!imageErrors[video.id] && video.cover_image_url ? (
                                    <img
                                        src={getImageUrl(video.cover_image_url)}
                                        alt={`${video.title} thumbnail`}
                                        onError={() => handleImageError(video.id)}
                                    />
                                ) : (
                                    <div className="video-thumbnail-placeholder">
                                        <FaVideo />
                                    </div>
                                )}

                                {/* Play Button Overlay */}
                                <button
                                    className={`play-button ${currentlyPlaying === video.id ? 'playing' : ''}`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        // Check for premium content before playing
                                        if (video.is_paid && !hasSufficientCredits(video.price || 1)) {
                                            setSelectedVideo(video);
                                            setShowInsufficientCreditsModal(true);
                                            return;
                                        }
                                        handlePlayClick(video);
                                    }}
                                    aria-label={currentlyPlaying === video.id ? 'Pause video' : 'Play video'}
                                    style={{
                                        position: 'absolute',
                                        top: '50%',
                                        left: '50%',
                                        transform: 'translate(-50%, -50%)',
                                        width: '72px',
                                        height: '72px',
                                        borderRadius: '50%',
                                        background: 'rgba(0, 0, 0, 0.7)',
                                        border: '3px solid white',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '28px',
                                        color: '#fff',
                                        cursor: 'pointer',
                                        zIndex: 100,
                                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)'
                                    }}
                                >
                                    {currentlyPlaying === video.id ? <FaPause /> : <FaPlay />}
                                </button>

                                {/* Duration Badge */}
                                {video.duration && (
                                    <span className="duration-badge">
                                        {formatDuration(video.duration)}
                                    </span>
                                )}

                                {/* Resolution Badge */}
                                {video.resolution && (
                                    <span className="resolution-badge">
                                        {video.resolution}
                                    </span>
                                )}
                            </div>

                            {/* Video Info */}
                            <div className="video-info">
                                {/* Paid/Free Badge */}
                                {video.is_paid && (
                                    <span className="premium-badge">
                                        <FaCrown className="badge-icon" />
                                        Premium
                                    </span>
                                )}

                                <h3 className="video-title">{video.title || 'Untitled'}</h3>
                                <p className="video-artist">
                                    {video.video?.artist || video.artist || video.creator?.username || 'Unknown Artist'}
                                </p>

                                {video.description && (
                                    <p className="video-description">
                                        {video.description.substring(0, 100)}
                                        {video.description.length > 100 && '...'}
                                    </p>
                                )}

                                {/* Video Meta */}
                                <div className="video-meta">
                                    {(() => {
                                        const raw = video.video?.ai_score ?? video.ai_score;
                                        if (raw === undefined || raw === null) return null;
                                        const n = Number(raw);
                                        if (!Number.isFinite(n) || n < 0) return null;
                                        return (
                                            <span className="video-ai-meter-pill" title={`AI Score: ${n}/5`}>
                                                <FaRobot className="ai-meter-icon" aria-hidden />
                                                {n}/5
                                            </span>
                                        );
                                    })()}
                                    {video.view_count > 0 && (
                                        <span className="video-views-pill">
                                            <FaEye className="eye-icon" />
                                            {video.view_count}
                                        </span>
                                    )}
                                    <button
                                        className="video-share-btn"
                                        onClick={(e) => handleShare(video, e)}
                                        title="Share video"
                                    >
                                        <FaShare className="share-icon" />
                                        Share
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="video-list-empty">
                    <FaVideo className="empty-icon" />
                    <p>No videos available</p>
                </div>
            )}
            {shareModal.isOpen && shareModal.video && (
                <ShareModal
                    isOpen={shareModal.isOpen}
                    onClose={() => setShareModal({ isOpen: false, video: null })}
                    shareUrl={getShareUrl(shareModal.video)}
                    title={shareModal.video.title || 'Untitled'}
                    type="video"
                />
            )}
            
            {showInsufficientCreditsModal && selectedVideo && (
                <InsufficientCreditsModal
                    isOpen={showInsufficientCreditsModal}
                    onClose={() => {
                        setShowInsufficientCreditsModal(false);
                        setSelectedVideo(null);
                    }}
                    requiredCredits={selectedVideo.price || 1}
                    currentCredits={creditBalance}
                    contentTitle={selectedVideo.title || 'this video'}
                    contentType="video"
                />
            )}
        </div>
    );
};

export default VideoList;
